import { gql } from '@apollo/client';
import { QuestionAnswerType } from './__generated__/graphql.ts';
import { StoredHighlightDataT } from '../contexts/highlight-provider/types.ts';

export const QUESTION_LOG_REVIEW_STATUS = {
  Unreviewed: 'Unreviewed',
  Reviewed: 'Reviewed',
} as const;

export type QuestionLogReviewStatus =
  (typeof QUESTION_LOG_REVIEW_STATUS)[keyof typeof QUESTION_LOG_REVIEW_STATUS];

export const QUESTION_LOG_SOURCE_TYPE = {
  Quiz: 'Quiz',
  Test: 'Test',
} as const;

export type QuestionLogSourceType =
  (typeof QUESTION_LOG_SOURCE_TYPE)[keyof typeof QUESTION_LOG_SOURCE_TYPE];

export const QUESTION_LOG_ITEM_TYPE = {
  Occurrence: 'Occurrence',
  EntryOnly: 'EntryOnly',
} as const;

export type QuestionLogItemType =
  (typeof QUESTION_LOG_ITEM_TYPE)[keyof typeof QUESTION_LOG_ITEM_TYPE];

export interface QuestionLogFilterInput {
  reviewStatus?: QuestionLogReviewStatus[];
  starred?: boolean;
  markedForReview?: boolean;
  sectionIds?: number[];
  domainIds?: number[];
  skillIds?: number[];
  qtypeIds?: number[];
}

export type QuestionLogTaxonomyFilter = Pick<
  QuestionLogFilterInput,
  'sectionIds' | 'domainIds' | 'skillIds' | 'qtypeIds'
>;

export interface QuestionLogPagingInput {
  page?: number;
  limit?: number;
}

interface QuestionLogLabel {
  id: number;
  name: string;
}

export interface QuestionLogSectionFacet extends QuestionLogLabel {
  count: number;
}

export interface QuestionLogDomainFacet extends QuestionLogLabel {
  count: number;
  sectionId: number;
}

export interface QuestionLogSkillFacet extends QuestionLogLabel {
  count: number;
  domainId: number;
}

export interface QuestionLogQtypeFacet extends QuestionLogLabel {
  count: number;
  sectionId: number;
  domainId?: number | null;
  skillId?: number | null;
}

export interface QuestionLogFacetCounts {
  sections: QuestionLogSectionFacet[];
  domains: QuestionLogDomainFacet[];
  skills: QuestionLogSkillFacet[];
  qtypes: QuestionLogQtypeFacet[];
}

export interface QuestionLogChoice {
  id: number;
  order: number;
  correct?: boolean | null;
  text?: string | null;
}

export interface QuestionLogQuestion {
  id: number;
  originalId: number;
  answerType?: QuestionAnswerType | null;
  prompt: string;
  content?: string | null;
  explanation?: string | null;
  section?: QuestionLogLabel | null;
  domain?: QuestionLogLabel | null;
  skill?: QuestionLogLabel | null;
  type1?: QuestionLogLabel | null;
  type2?: QuestionLogLabel | null;
  choices: QuestionLogChoice[];
}

export interface QuestionLogEntry {
  itemKey: string;
  itemType: QuestionLogItemType;
  occurrenceId?: number | null;
  questionEntryId: number;
  isStarredOnly: boolean;
  reviewStatus: QuestionLogReviewStatus;
  starred: boolean;
  starredAt?: Date | string | null;
  markedForReview: boolean;
  markedForReviewAt?: Date | string | null;
  reviewedAt?: Date | string | null;
  wrongCount: number;
  firstWrongAt?: Date | string | null;
  lastWrongAt?: Date | string | null;
  lastAttemptCompletedAt?: Date | string | null;
  lastSourceType?: QuestionLogSourceType | null;
  lastSourceName?: string | null;
  lastAttemptId?: number | null;
  lastSectionOrder?: number | null;
  lastModuleOrder?: number | null;
  lastQuestionOrder?: number | null;
  lastChoiceId?: number | null;
  lastText?: string | null;
  reviewAnnotations?: StoredHighlightDataT[] | null;
  questionId: number;
  question: QuestionLogQuestion;
}

export interface GetQuestionLogListQuery {
  questionLog: {
    getQuestionLog: {
      page?: number | null;
      limit?: number | null;
      total?: number | null;
      results: QuestionLogEntry[];
    };
  };
}

export interface GetQuestionLogListQueryVariables {
  paging?: QuestionLogPagingInput;
  filter?: QuestionLogFilterInput;
}

export interface GetQuestionLogFacetsQuery {
  questionLog: {
    getQuestionLogFacets: QuestionLogFacetCounts;
  };
}

export interface GetQuestionLogFacetsQueryVariables {
  filter?: QuestionLogFilterInput;
}

export interface GetQuestionLogStatusesQuery {
  questionLog: {
    getQuestionLogStatuses: Array<
      Pick<
        QuestionLogEntry,
        | 'itemKey'
        | 'itemType'
        | 'occurrenceId'
        | 'questionEntryId'
        | 'isStarredOnly'
        | 'questionId'
        | 'reviewStatus'
        | 'reviewedAt'
        | 'starred'
        | 'starredAt'
        | 'markedForReview'
        | 'markedForReviewAt'
        | 'wrongCount'
        | 'reviewAnnotations'
      >
    >;
  };
}

export interface GetQuestionLogStatusesQueryVariables {
  questionIds: number[];
  sourceType?: QuestionLogSourceType;
  attemptId?: number;
}

export const GET_QUESTION_LOG_LIST = gql(/* GraphQL */ `
  query getQuestionLogList($paging: PagingInput, $filter: QuestionLogFilterInput) {
    questionLog {
      getQuestionLog(paging: $paging, filter: $filter) {
        page
        limit
        total
        results {
          itemKey
          itemType
          occurrenceId
          questionEntryId
          isStarredOnly
          reviewStatus
          starred
          starredAt
          markedForReview
          markedForReviewAt
          reviewedAt
          wrongCount
          firstWrongAt
          lastWrongAt
          lastAttemptCompletedAt
          lastSourceType
          lastSourceName
          lastAttemptId
          lastSectionOrder
          lastModuleOrder
          lastQuestionOrder
          lastChoiceId
          lastText
          reviewAnnotations
          questionId
          question {
            id
            originalId
            answerType
            prompt
            content
            explanation
            section {
              id
              name
            }
            domain {
              id
              name
            }
            skill {
              id
              name
            }
            type1 {
              id
              name
            }
            type2 {
              id
              name
            }
            choices {
              correct
              id
              order
              text
            }
          }
        }
      }
    }
  }
`);

export const GET_QUESTION_LOG_FACETS = gql(/* GraphQL */ `
  query getQuestionLogFacets($filter: QuestionLogFilterInput) {
    questionLog {
      getQuestionLogFacets(filter: $filter) {
        sections {
          id
          name
          count
        }
        domains {
          id
          name
          count
          sectionId
        }
        skills {
          id
          name
          count
          domainId
        }
        qtypes {
          id
          name
          count
          sectionId
          domainId
          skillId
        }
      }
    }
  }
`);

export const GET_QUESTION_LOG_STATUSES = gql(/* GraphQL */ `
  query getQuestionLogStatuses($questionIds: [Int!]!, $sourceType: QuestionLogSourceType, $attemptId: Int) {
    questionLog {
      getQuestionLogStatuses(questionIds: $questionIds, sourceType: $sourceType, attemptId: $attemptId) {
        itemKey
        itemType
        occurrenceId
        questionEntryId
        isStarredOnly
        questionId
        reviewStatus
        reviewedAt
        starred
        starredAt
        markedForReview
        markedForReviewAt
        wrongCount
        reviewAnnotations
      }
    }
  }
`);

export const MARK_QUESTION_LOG_REVIEWED = gql(/* GraphQL */ `
  mutation markQuestionLogReviewed($input: QuestionLogQuestionInput!) {
    questionLog {
      markReviewed(input: $input) {
        id
        questionId
        reviewStatus
        reviewedAt
        starred
        starredAt
        wrongCount
      }
    }
  }
`);

export const MARK_QUESTION_LOG_UNREVIEWED = gql(/* GraphQL */ `
  mutation markQuestionLogUnreviewed($input: QuestionLogQuestionInput!) {
    questionLog {
      markUnreviewed(input: $input) {
        id
        questionId
        reviewStatus
        reviewedAt
        starred
        starredAt
        wrongCount
      }
    }
  }
`);

export const MARK_QUESTION_LOG_OCCURRENCE_REVIEWED = gql(/* GraphQL */ `
  mutation markQuestionLogOccurrenceReviewed($input: QuestionLogOccurrenceInput!) {
    questionLog {
      markOccurrenceReviewed(input: $input) {
        id
        questionId
        reviewStatus
        reviewedAt
      }
    }
  }
`);

export const MARK_QUESTION_LOG_OCCURRENCE_UNREVIEWED = gql(/* GraphQL */ `
  mutation markQuestionLogOccurrenceUnreviewed($input: QuestionLogOccurrenceInput!) {
    questionLog {
      markOccurrenceUnreviewed(input: $input) {
        id
        questionId
        reviewStatus
        reviewedAt
      }
    }
  }
`);

export const TOGGLE_QUESTION_LOG_STAR = gql(/* GraphQL */ `
  mutation toggleQuestionLogStar($input: UpdateQuestionLogStarInput!) {
    questionLog {
      toggleStar(input: $input) {
        id
        questionId
        reviewStatus
        reviewedAt
        starred
        starredAt
        wrongCount
      }
    }
  }
`);

export const TOGGLE_QUESTION_LOG_MARKED_FOR_REVIEW = gql(/* GraphQL */ `
  mutation toggleQuestionLogMarkedForReview($input: UpdateQuestionLogMarkedForReviewInput!) {
    questionLog {
      toggleMarkedForReview(input: $input) {
        id
        questionId
        reviewStatus
        reviewedAt
        starred
        starredAt
        markedForReview
        markedForReviewAt
        wrongCount
      }
    }
  }
`);

export const SAVE_QUESTION_LOG_REVIEW_ANNOTATIONS = gql(/* GraphQL */ `
  mutation saveQuestionLogReviewAnnotations($input: UpdateQuestionLogReviewAnnotationsInput!) {
    questionLog {
      saveReviewAnnotations(input: $input) {
        id
        questionId
        reviewAnnotations
      }
    }
  }
`);
